import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
  content: string;
  children: React.ReactElement;
  delay?: number; // milliseconds
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, delay = 200 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  // Clean up on unmount — prevents orphaned tooltips when react-select destroys options
  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = tooltipRef.current?.offsetHeight || 30;

      // Position above the element by default
      let top = rect.top - tooltipHeight - 8;
      let left = rect.left;

      // If tooltip would go off top of screen, position below instead
      if (top < 10) {
        top = rect.bottom + 8;
      }

      // Ensure tooltip doesn't go off right edge
      if (tooltipRef.current) {
        const tooltipWidth = tooltipRef.current.offsetWidth;
        if (left + tooltipWidth > window.innerWidth - 10) {
          left = window.innerWidth - tooltipWidth - 10;
        }
      }

      // Ensure tooltip doesn't go off left edge
      if (left < 10) {
        left = 10;
      }

      setPosition({ top, left });
    }
  }, [isVisible]);

  const tooltipElement = isVisible ? (
    <div
      ref={tooltipRef}
      className="fixed z-[99999] px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-normal max-w-xs break-words pointer-events-none"
      style={{ top: position.top, left: position.left }}
    >
      {content}
    </div>
  ) : null;

  return (
    <div
      ref={triggerRef}
      className="relative inline-block w-full"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {ReactDOM.createPortal(tooltipElement, document.body)}
    </div>
  );
};

export default Tooltip;
